/**
 * THE ANSWER KEY, arranged the way a student will meet it.
 *
 * A vertical stack: the unsorted source, then one section per bin, with items dragged between them.
 * The teacher builds the key by putting things where they belong, as Ordering's custom-order editor
 * does — WYSIWYG, rather than a table of item-to-bin pickers nobody can read.
 *
 * ## The grouping is a VIEW
 *
 * Items are a flat array on the block, and everything that hangs off that array — media options,
 * versions, scenario following — is Ordering's plumbing unchanged. Nesting items inside bins would
 * fork all of it. So a drag between two sections rewrites one field on one item, and the sections
 * are only how the key is drawn.
 *
 * ## Why the source is always visible here
 *
 * On the STUDENT surface the source can give way to the bins once it empties, because the content
 * migrates. In the editor there is nothing to reclaim: an item left in the source means the key is
 * unfinished, and hiding the evidence of that would be hiding the one thing the teacher still has
 * to do.
 *
 * Authoring is always this vertical stack, whatever layout the students get — a long scroll is fine
 * for authoring, and it keeps the editor from having to be three layouts of its own.
 */
import { DndContext, DragOverlay, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Image as ImageIcon, ImageOff, Images, Trash2, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAnimatedAssetIds } from './animated-image';
import { FormulaField } from './formula-field';
import { stripHtmlForLabel } from './formula-plaintext';
import { LibraryModal } from './library-modal';
import { RichTextEditor } from './rich-text-editor';
import { sortCollisionDetection } from './sort-placement';

import type { SharedEditorToolbar } from './use-shared-editor-toolbar';
import type { TaskVariable } from '@/types/experience';
import type { AngleMode, SortBin, SortItem, SortKey } from '@agavi/blocks';
import type { Editor } from '@tiptap/react';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';
import { assetsApi, peekAsset, type AssetSummary } from '@/lib/assets-api';

/** The id the source section drops into. Not a bin — "not placed" is a state, not a category, and
 *  the completeness check the whole block hangs on depends on telling them apart. */
export const UNSORTED = '__unsorted__';

interface SortBinsEditorProps {
  /** The block's ONE toolbar. Every editor here hides its own and registers into this, which is
   *  what keeps a row from drawing a second full toolbar under the sticky one. */
  sharedToolbar: SharedEditorToolbar;
  items: SortItem[];
  bins: SortBin[];
  sortKey: SortKey;
  /** One item moved into one section. `UNSORTED` takes it back out of the key. */
  onPlace: (itemId: string, binId: string, atIndex?: number) => void;
  onBinLabelChange: (binId: string, label: string) => void;
  onBinAdd: () => void;
  onBinRemove: (binId: string) => void;
  /** `binId` is where the new item lands — the source when absent. */
  onItemAdd: (binId?: string) => void;
  onItemRemove: (itemId: string) => void;
  onItemTextChange: (itemId: string, text: string) => void;
  /** Equations mode: each row authors a formula instead of rich text. See `SortBlockData.itemsAsEquations`. */
  formulaMode?: boolean;
  onItemFormulaChange?: (itemId: string, formula: string) => void;
  variables?: TaskVariable[];
  rolledValues?: Record<string, string>;
  angleModeFor?: (itemId: string) => AngleMode;
  /** Which bin is armed for deletion, held by the parent so arming one disarms the others. */
  armedBinId: string | null;
  setArmedBinId: (id: string | null) => void;
  armedItemId: string | null;
  setArmedItemId: (id: string | null) => void;
  /** At the cap, the add control goes rather than being offered and refused. */
  canAddBin: boolean;
  /** Which controls an issue names, from `issueFieldIds` — ONE derivation feeds both this ring and
   *  the panel that explains it, so the two cannot disagree about which row is at fault. */
  issueFields: ReadonlySet<string>;
  /** For the per-item media picker — the same props MCQ and Ordering pass theirs. */
  experienceId: string;
  taskId?: string;
  taskBlocks?: unknown[];
  onMediaChange: (itemId: string, media: SortItem['media'], seedLabel?: string) => void;
}

function ItemRow({
  item, armed, onArm, onRemove, onTextChange, canRemove, toolbar, editorsRef, flagged,
  experienceId, taskId, taskBlocks, onMediaChange, animatedAssetIds,
  formulaMode, onFormulaChange, variables, rolledValues, angleMode,
}: {
  item: SortItem;
  flagged: boolean;
  armed: boolean;
  onArm: (armed: boolean) => void;
  onRemove: () => void;
  onTextChange: (text: string) => void;
  canRemove: boolean;
  toolbar: SharedEditorToolbar;
  editorsRef: React.MutableRefObject<Map<string, Editor>>;
  experienceId: string;
  taskId?: string;
  taskBlocks?: unknown[];
  onMediaChange: (itemId: string, media: SortItem['media'], seedLabel?: string) => void;
  animatedAssetIds: ReadonlySet<string>;
  /** The content editor becomes a plain `FormulaField` — rich text off, media suppressed. Identical
   *  contract to Ordering's and MCQ's own `formulaMode`; the four props below are read only here. */
  formulaMode?: boolean;
  onFormulaChange?: (formula: string) => void;
  variables?: TaskVariable[];
  rolledValues?: Record<string, string>;
  angleMode?: AngleMode;
}) {
  const { t } = useTranslation();
  const [showLibrary, setShowLibrary] = useState(false);
  /** Two presses to drop a placed asset, and row-LOCAL: arming the media must not arm the row. */
  const [mediaArmed, setMediaArmed] = useState(false);
  const labelInputRef = useRef<HTMLInputElement>(null);
  // Seeded from the cache, so a placed asset is on screen in the first render rather than after a
  // round trip — see `peekAsset`.
  const [cachedAsset, setCachedAsset] = useState<AssetSummary | null>(
    () => (item.media?.assetId ? peekAsset(item.media.assetId) ?? null : null),
  );
  /* The Label's OWN value, seeded once. Deriving it from `item.text` each render re-trimmed a
     trailing space the instant it was typed, so spaces could never survive mid-word. */
  const [labelDraft, setLabelDraft] = useState(() => item.text.replace(/<[^>]*>/g, '').trim());

  useEffect(() => {
    if (!item.media?.assetId) { setCachedAsset(null); return; }
    if (cachedAsset?.id === item.media.assetId) return;
    let cancelled = false;
    assetsApi.get(item.media.assetId).then(a => { if (!cancelled) setCachedAsset(a); }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.media?.assetId]);

  function handleMediaSelect(asset: AssetSummary, chosen: { altText?: string; transcript?: string }) {
    const existing = item.text.replace(/<[^>]*>/g, '').trim();
    /* Seeded from the FILENAME when there is no label yet. This field is never shown to students,
       so a default here cannot mislead anyone — it only has to be distinguishable, and the
       teacher's own filename already is. */
    const seeded = existing || asset.filename.replace(/\.[^.]+$/, '');
    onMediaChange(item.id, { kind: asset.kind, assetId: asset.id, animated: !!asset.animated, altText: chosen.altText, transcript: chosen.transcript }, existing ? undefined : seeded);
    setCachedAsset(asset);
    setShowLibrary(false);
    setLabelDraft(seeded);
    requestAnimationFrame(() => { labelInputRef.current?.focus(); });
  }

  const animated = !!item.media && item.media.kind === 'image' && animatedAssetIds.has(item.media.assetId);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-field-id={item.id}
      className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
        /* HALF the usual bleed, said locally, which is what `ARMED_DELETE_BLEED`'s own doc
           asks a row with a neighbour to do. These rows sit inside a bin's `p-2` box, so the
           standard `-mx-2` lands the ring exactly on the section border and on the input borders
           either side of it. `-mx-1` leaves the hairline its own gap. */
        armed ? `rounded-lg ${ARMED_DELETE_PAINT} -mx-1 px-1 -my-1 py-1` : ''
      }`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.sort.dragItem', { item: item.text || t('blocks.sort.untitledItem') })}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      {/* FRAMED when it holds media, for the reason MCQ and Ordering both frame theirs: a media
          item is a pill, an alt-text field, sometimes a motion description and always a label —
          four controls deep, and stacked they run together with no way to see where one item
          starts. A text item is one editor box, and the box is its own edge. */}
      <div className={`flex min-w-0 flex-1 flex-col gap-1 ${
        item.media && !formulaMode ? 'rounded-lg border border-ws-border bg-ws-btn/40 p-2' : 'rounded'
      } ${flagged ? 'ring-1 ring-ws-error-fg' : ''}`}>
        {formulaMode ? (
          /* BEFORE `item.media`, as Ordering's row checks it: equations mode suppresses media
             outright, and the toggle handler already strips it from every item the moment the mode
             turns on. Reaching this branch with media still attached means props from a render in
             flight, so this ordering is the belt to that handler's braces, not the enforcement. */
          <FormulaField
            fieldId={`items.${item.id}.formula`}
            value={item.formula ?? ''}
            onChange={(formula) => onFormulaChange?.(formula)}
            variables={variables ?? []}
            rolledValues={rolledValues ?? {}}
            ariaLabel={t('blocks.sort.itemFormulaLabel')}
            hideEval
            hasError={flagged}
            angleMode={angleMode}
            onFocus={toolbar.disconnect}
          />
        ) : item.media ? (
          <>
            {/* The paint covers what the next press CLEARS — the label stays, so it is outside. */}
            <div className={`flex flex-col gap-1 rounded p-1.5 ${mediaArmed ? ARMED_DELETE_PAINT : ''}`}>
              <div className='flex items-center gap-1.5'>
                <button
                  type='button'
                  onClick={() => setShowLibrary(true)}
                  aria-label={t('blocks.sort.swapMedia')}
                  className='flex min-w-0 flex-1 items-center gap-2 rounded-md border border-ws-border bg-ws-btn px-2 py-1 text-left hover:bg-ws-hover'
                >
                  {item.media.kind === 'image'
                    ? (cachedAsset?.urls.thumb
                      ? <img src={cachedAsset.urls.thumb} alt='' className='h-7 w-7 shrink-0 rounded object-cover' />
                      : <ImageIcon className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />)
                    : <Volume2 className='h-5 w-5 shrink-0 text-ws-muted' aria-hidden='true' />}
                  <span className='min-w-0 flex-1 truncate text-xs text-ws-text'>
                    {cachedAsset?.filename ?? t('blocks.sort.itemMediaLoading')}
                  </span>
                </button>
                {/* NAMES WHAT IT REMOVES, and carries the medium's own glyph: two adjacent plain
                    trash icons said nothing about which one dropped the picture. */}
                <IconButton
                  type='button' variant='destructive' size='sm'
                  isArmed={mediaArmed}
                  aria-label={mediaArmed ? t('blocks.actions.confirmDelete') : t('blocks.sort.removeMedia')}
                  onClick={() => {
                    if (!mediaArmed) { setMediaArmed(true); return; }
                    setMediaArmed(false);
                    onMediaChange(item.id, undefined);
                  }}
                  onPointerLeave={(e) => { if (e.pointerType !== 'touch' && mediaArmed) setMediaArmed(false); }}
                  onBlur={() => setMediaArmed(false)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && mediaArmed) { e.stopPropagation(); setMediaArmed(false); } }}
                >
                  {item.media.kind === 'image' ? <ImageOff className='h-3.5 w-3.5' /> : <VolumeX className='h-3.5 w-3.5' />}
                </IconButton>
              </div>
              {/* Alt text for images only. Audio deliberately has no transcript field here — the
                  same accepted limitation MCQ's and Ordering's media options carry. */}
              {item.media.kind === 'image' && (
                <TextField
                  variant='prose'
                  value={item.media.altText ?? ''}
                  onChange={v => onMediaChange(item.id, { ...item.media!, altText: v })}
                  ariaLabel={t('blocks.image.altText')}
                  placeholder={t('blocks.image.altText')}
                  onFocus={toolbar.disconnect}
                  className='text-xs' />
              )}
              {/* REQUIRED on an animation, per WCAG 1.2.1: a still alt text describes the frame, and
                  what an animation means is what MOVES. */}
              {animated && (
                <TextField
                  variant='prose'
                  value={item.media.motionDescription ?? ''}
                  onChange={v => onMediaChange(item.id, { ...item.media!, motionDescription: v })}
                  ariaLabel={t('blocks.image.motionDescription')}
                  placeholder={t('blocks.image.motionDescription')}
                  onFocus={toolbar.disconnect}
                  className='text-xs' />
              )}
            </div>
            {/* The always-required Label: the internal identifier feedback, Flow and reports read,
                never shown to a student once media is present. Deliberately a PLAIN input — a
                rich-text Label is a structural hazard, not just an inconsistency. */}
            <TextField
              variant='literal'
              inputRef={labelInputRef}
              value={labelDraft}
              onChange={v => { setLabelDraft(v); onTextChange(v); }}
              onFocus={toolbar.disconnect}
              ariaLabel={t('blocks.sort.itemLabelPlaceholder')}
              placeholder={t('blocks.sort.itemLabelPlaceholder')}
              className='w-full' />
          </>
        ) : (
          <div className='flex min-w-0 items-center gap-1.5'>
            {/* RICH TEXT, like every other item row — an item can carry a variable chip, a recall
                chip or a formula, none of which a plain input holds. `hideToolbar` is what makes
                it share the block's one toolbar instead of growing another. */}
            <div className='min-w-0 flex-1'>
              <RichTextEditor
                fieldId={`${item.id}.text`}
                content={item.text}
                onChange={onTextChange}
                /* The shared editor takes its ACCESSIBLE NAME from the placeholder — see its own
                   `aria-label: placeholder ?? ...`. Without one the row is an unnamed text box. */
                placeholder={t('blocks.sort.itemPlaceholder')}
                editorClassName='min-h-6'
                hideToolbar
                onEditorReady={(e) => { editorsRef.current.set(item.id, e); }}
                onEditorFocus={() => {
                  const e = editorsRef.current.get(item.id);
                  if (e) { toolbar.activeEditorRef.current = e; toolbar.setActiveEditor(e); }
                }}
              />
            </div>
            <IconButton type='button' size='sm' aria-label={t('blocks.sort.insertMedia')} onClick={() => setShowLibrary(true)}>
              <Images className='h-3.5 w-3.5' />
            </IconButton>
          </div>
        )}
      </div>

      {showLibrary && (
        <LibraryModal
          assetKind='any'
          experienceId={experienceId}
          currentAssetId={item.media?.assetId ?? null}
          currentTaskId={taskId ?? ''}
          currentTaskBlocks={taskBlocks as never}
          onSelect={handleMediaSelect}
          onClose={() => setShowLibrary(false)}
        />
      )}
      <IconButton
        type='button'
        variant='destructive'
        size='sm'
        isArmed={armed}
        disabled={!canRemove}
        aria-label={armed ? t('blocks.actions.confirmDelete') : t('blocks.sort.removeItem')}
        onClick={() => { if (armed) { onArm(false); onRemove(); } else onArm(true); }}
        onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armed) onArm(false); }}
        onBlur={() => onArm(false)}
        onKeyDown={(e) => { if (e.key === 'Escape' && armed) { e.stopPropagation(); onArm(false); } }}
      >
        <Trash2 className='h-3.5 w-3.5' />
      </IconButton>
    </div>
  );
}

/** One droppable section — the source or a bin. Rendered the same way so a drag reads identically
 *  wherever it lands, and distinguished by its heading rather than by its shape. */
function Section({ id, heading, itemIds, children, onItemAdd, addLabel, highlighted }: {
  id: string;
  heading: React.ReactNode;
  itemIds: string[];
  children: React.ReactNode;
  /** `binId` is where the new item lands — the source when absent. */
  onItemAdd: (binId?: string) => void;
  addLabel: string;
  /** Set by the parent when the drag's current target belongs to THIS section — see `lit`. */
  highlighted?: boolean;
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useSortable({ id, data: { isSection: true } });
  /* `isOver` ALONE IS NOT ENOUGH. It is true only while the pointer is over the section's own
     node — move onto an ITEM inside another bin and the item becomes the `over` target, so the
     bin you are actually dropping into stops highlighting. Measured: a cross-bin drag showed no
     highlight anywhere for most of its travel, which read as "this will not drop", even though
     it worked. The parent computes which section owns the current target and says so. */
  const lit = isOver || highlighted;
  return (
    <section
      ref={setNodeRef}
      data-field-id={id}
      className={`rounded-lg border p-2 transition-colors ${
        lit ? 'border-ws-primary bg-ws-primary/5' : 'border-ws-border bg-ws-btn/40'
      }`}
    >
      {heading}
      {/* A visible floor even when empty: a bin with no items must still read as a bin, or an
          unfinished key looks like a broken layout. */}
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {/* THE PLACEMENT LIST IS ITS OWN FIELD, separately from the bin.
            The `<section>` above is tagged with the bin's id, which is what an edit to the bin's
            LABEL is named. Moving an item in or out edits the answer key instead, and that op is
            named `key.placements.<binId>` — so with only the section tagged, an undo of the single
            most consequential thing a teacher does here could not point at anything and fell back
            to flashing the whole block. The source list is deliberately not tagged: nothing in the
            key names it. See `undo-field-reveal.test`. */}
        {/* `mt-3`, not `mt-1.5`: six pixels put the first item against the bin's name and the box
            read as one crowded block. CENTRED when empty, both axes — the note used to sit hard
            against the top-left of the empty area, which reads as a layout fault rather than as an
            empty bin. `text-sm italic text-ws-muted` is the app's one empty-state treatment, the
            same one "No question yet" and "No image yet" use. */}
        <div data-field-id={id === UNSORTED ? undefined : `key.placements.${id}`}
          /* ONE ROW TALL when empty — the same `min-h-9` a filled bin's first row occupies. A taller
             well looked deliberate and then COLLAPSED the moment the first item arrived, which
             reads as the bin shrinking rather than as the item filling it. */
          className={`mt-3 flex min-h-9 flex-col gap-1.5 ${itemIds.length === 0
            ? 'items-center justify-center' : ''}`}>
          {/* An empty bin says so, in the app's one empty-state treatment — `text-sm italic
              text-ws-muted`, the same note "No question yet" and "No image yet" are drawn in. A
              bare floor reads as a layout that failed rather than a bin with nothing in it. */}
          {itemIds.length === 0
            ? <p className='text-sm italic text-ws-muted'>{t('blocks.sort.noItems')}</p>
            : children}
        </div>
      </SortableContext>
      {/* PER SECTION, because adding an item is adding it SOMEWHERE — a single add at the bottom
          drops every new item into the source and leaves the teacher to drag it where they
          already knew it was going.
          Full width, NOT `inline`: this grows the bin's own list, and `inline` is for adds that
          are ALTERNATIVES to each other. FITB's per-blank Add Word and Numerical's nested Add Unit
          Option are the same shape and both render full width. */}
      {/* `mt-3` here too: the add control was touching the last item, so the list and the way to
          extend it read as one run of rows. */}
      <div className='mt-3'>
        <AddRowButton label={addLabel} onClick={onItemAdd} />
      </div>
    </section>
  );
}

export function SortBinsEditor({
  sharedToolbar, items, bins, sortKey, onPlace, onBinLabelChange, onBinAdd, onBinRemove,
  onItemAdd, onItemRemove, onItemTextChange,
  armedBinId, setArmedBinId, armedItemId, setArmedItemId, canAddBin, issueFields,
  experienceId, taskId, taskBlocks, onMediaChange,
  formulaMode, onItemFormulaChange, variables, rolledValues, angleModeFor,
}: SortBinsEditorProps) {
  const { t } = useTranslation();
  /* WHICH assets animate — only the hook knows, and a motion description is required for those
     and meaningless for the rest. De-duplicated across rows by id inside it. */
  const animatedAssetIds = useAnimatedAssetIds(items.flatMap(i => (i.media ? [i.media.assetId] : [])));
  /** Every row's editor, so a focus can hand the shared toolbar the right one. */
  const editorsRef = useRef<Map<string, Editor>>(new Map());
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor),
  );
  /** The row riding under the cursor, and the section it is currently over. */
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overSection, setOverSection] = useState<string | null>(null);
  const byId = new Map(items.map(i => [i.id, i]));
  const placed = new Set(Object.values(sortKey.placements).flat());
  const unsorted = items.filter(i => !placed.has(i.id)).map(i => i.id);

  /**
   * WHICH SECTION A TARGET BELONGS TO — the same question `handleDragEnd` answers to place the
   * item, asked during the drag so the destination can light up. A section id is itself; an item
   * id resolves to whichever bin holds it, or the source when nothing does.
   */
  function sectionOf(overId: string): string {
    if (overId === UNSORTED || bins.some(b => b.id === overId)) return overId;
    return bins.find(b => (sortKey.placements[b.id] ?? []).includes(overId))?.id ?? UNSORTED;
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(null);
    setOverSection(null);
    if (!over) return;
    const itemId = String(active.id);
    const overId = String(over.id);
    /* Dropped on a SECTION: append. Dropped on an ITEM: land where that item is, so a drag can
       place between two others. `over.data` carries which, set by `Section` above. */
    if (over.data.current?.isSection) {
      onPlace(itemId, overId);
      return;
    }
    const landedIn = bins.find(b => (sortKey.placements[b.id] ?? []).includes(overId));
    if (!landedIn) { onPlace(itemId, UNSORTED); return; }
    onPlace(itemId, landedIn.id, (sortKey.placements[landedIn.id] ?? []).indexOf(overId));
  }

  const row = (id: string) => {
    const item = byId.get(id);
    if (!item) return null;
    return (
      <ItemRow
        key={id}
        item={item}
        armed={armedItemId === id}
        onArm={(a) => setArmedItemId(a ? id : null)}
        onRemove={() => onItemRemove(id)}
        onTextChange={(text) => onItemTextChange(id, text)}
        canRemove={items.length > 1}
        toolbar={sharedToolbar}
        editorsRef={editorsRef}
        flagged={issueFields.has(id)}
        experienceId={experienceId}
        taskId={taskId}
        taskBlocks={taskBlocks}
        onMediaChange={onMediaChange}
        animatedAssetIds={animatedAssetIds}
        formulaMode={formulaMode}
        onFormulaChange={(formula) => onItemFormulaChange?.(id, formula)}
        variables={variables}
        rolledValues={rolledValues}
        angleMode={angleModeFor?.(id)}
      />
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={sortCollisionDetection}
      onDragStart={({ active }) => setActiveId(String(active.id))}
      onDragOver={({ over }) => setOverSection(over ? sectionOf(String(over.id)) : null)}
      onDragCancel={() => { setActiveId(null); setOverSection(null); }}
      onDragEnd={handleDragEnd}
    >
      <div className='flex flex-col gap-2'>
        <Section
          id={UNSORTED}
          highlighted={overSection === UNSORTED}
          itemIds={unsorted}
          onItemAdd={() => onItemAdd()}
          addLabel={t('blocks.sort.addItem')}
          heading={
            <h4 className='text-xs font-semibold uppercase tracking-wider text-ws-muted'>
              {t('blocks.sort.unsortedHeading', { count: unsorted.length })}
            </h4>
          }
        >
          {unsorted.map(row)}
        </Section>

        {bins.map((bin) => {
          const ids = sortKey.placements[bin.id] ?? [];
          return (
            <Section
              key={bin.id}
              id={bin.id}
              highlighted={overSection === bin.id}
              itemIds={ids}
              onItemAdd={() => onItemAdd(bin.id)}
              addLabel={t('blocks.sort.addItem')}
              heading={
                <div className={`flex items-center gap-2 ${
                  armedBinId === bin.id ? `rounded-lg ${ARMED_DELETE_PAINT} -mx-1 px-1 -my-1 py-1` : ''
                }`}>
                  {/* RICH TEXT: a bin label takes a variable chip or a recall chip, so the bin a
                      student sees can be named by their own earlier answer. A plain input cannot
                      hold either. */}
                  <div className={`min-w-0 flex-1 rounded ${issueFields.has(bin.id) ? 'ring-1 ring-ws-error-fg' : ''}`}>
                    <RichTextEditor
                      fieldId={`${bin.id}.label`}
                      content={bin.label}
                      onChange={(label) => onBinLabelChange(bin.id, label)}
                      placeholder={t('blocks.sort.binPlaceholder')}
                      editorClassName='min-h-6'
                      hideToolbar
                      onEditorReady={(e) => { editorsRef.current.set(bin.id, e); }}
                      onEditorFocus={() => {
                        const e = editorsRef.current.get(bin.id);
                        if (e) { sharedToolbar.activeEditorRef.current = e; sharedToolbar.setActiveEditor(e); }
                      }}
                    />
                  </div>
                  <IconButton
                    type='button'
                    variant='destructive'
                    size='sm'
                    isArmed={armedBinId === bin.id}
                    disabled={bins.length <= 2}
                    aria-label={armedBinId === bin.id
                      ? t('blocks.sort.confirmRemoveBin', { count: ids.length })
                      : t('blocks.sort.removeBin')}
                    onClick={() => {
                      if (armedBinId === bin.id) { setArmedBinId(null); onBinRemove(bin.id); }
                      else setArmedBinId(bin.id);
                    }}
                    onPointerLeave={(e) => { if (e.pointerType !== 'touch' && armedBinId === bin.id) setArmedBinId(null); }}
                    onBlur={() => setArmedBinId(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape' && armedBinId === bin.id) { e.stopPropagation(); setArmedBinId(null); } }}
                  >
                    <Trash2 className='h-3.5 w-3.5' />
                  </IconButton>
                </div>
              }
            >
              {ids.map(row)}
            </Section>
          );
        })}

        {/* Add Bin stays at the bottom and full-width: it grows the LIST OF SECTIONS, which is
            what a full-width dashed row below them means everywhere else in the app. */}
        <AddRowButton label={t('blocks.sort.addBin')} onClick={onBinAdd} disabled={!canAddBin} />
      </div>
      {/**
        * THE ROW UNDER THE CURSOR, which the editor had none of.
        *
        * A dragged row went to `opacity-40` IN PLACE and nothing travelled with the pointer, so
        * crossing into another bin showed an empty hole where the item had been, the cursor
        * turning into a text caret over the rich-text rows it passed, and no card anywhere. The
        * drop worked; it simply looked like it would not. The student surface has had an overlay
        * for exactly this reason — this is the editor catching up.
        *
        * Deliberately a PLAIN chip, not the real row: the row carries a rich-text editor, a media
        * pill and an armed trash, none of which should exist twice in the document or be
        * interactive while in flight.
        */}
      <DragOverlay>
        {activeId !== null ? (
          <div className='flex items-center gap-2 rounded-md border border-ws-primary bg-ws-surface px-2 py-1.5 text-sm text-ws-text shadow-lg'>
            <GripVertical className='h-4 w-4 shrink-0 text-ws-muted' aria-hidden='true' />
            <span className='min-w-0 truncate'>
              {stripHtmlForLabel(byId.get(activeId)?.text ?? '').trim()
                || t('blocks.sort.untitledItem')}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
